/**
 * This file contains [Typescript type
 * predicates](https://www.typescriptlang.org/docs/handbook/advanced-types.html#using-type-predicates)
 * that allow us to safely narrow the type bounds for
 * StructuredTableColumns.  This is useful when we want to do things
 * like transform a StructuredTableColumn into a ColumnDefinition.
 * Instead of having a switch statement and then a cast statement, we
 * can do things like the following:
 *
 *   const structuredTableColumn: StructuredTableColumn = getStructuredTableColumn();
 *   if (isTagValueStructuredTableColumn(structuredTableColumn)) {
 *       return structuredTableColumn.selector;
 *   } else if (isQuantityStructuredTableColumn(structuredTableColumn)) {
 *       return structuredTableColumn.units;
 *   }
 */
import {getColumnAdapter} from 'notebook/structured-tables/StructuredTableColumnAdapters';
import {
  ExistingBioentityReferenceStructuredTableColumn,
  BoxStructuredTableColumn,
  ConcentrationStructuredTableColumn,
  ContainersStructuredTableColumn,
  CustomStructuredTableColumn,
  ExistingBioentityStructuredTableColumn,
  MeasurementAmountStructuredTableColumn,
  ParentStorableStructuredTableColumn,
  PartiesStructuredTableColumn,
  PlateStructuredTableColumn,
  PositionStructuredTableColumn,
  ProcedureConditionReplicatesStructuredTableColumn,
  ProcessOutputStructuredTableColumn,
  ProcessTaskStatusStructuredTableColumn,
  ProcessTaskStructuredTableColumn,
  QuantityStructuredTableColumn,
  SampleRestrictionStatusStructuredTableColumn,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  TagValueStructuredTableColumn,
  UnitReferenceStructuredTableColumn,
  UnitSelectorStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

export function isTagValueStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is TagValueStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.TAG_VALUE;
}

export function isExistingBioentityStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is ExistingBioentityStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.EXISTING_BIOENTITY;
}

export function isExistingBioentityReferenceStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is ExistingBioentityReferenceStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.EXISTING_BIOENTITY_REFERENCE;
}

export function isQuantityStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is QuantityStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.QUANTITY;
}

export function isConcentrationStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is ConcentrationStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.CONCENTRATION;
}

export function isParentStorableStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is ParentStorableStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.PARENT_STORABLE;
}

export function isPositionStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is PositionStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.POSITION;
}

export function isPartiesStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is PartiesStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.PARTIES;
}

export function isSampleRestrictionStatusStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is SampleRestrictionStatusStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.SAMPLE_RESTRICTION_STATUS;
}

export function isUnitSelectorStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is UnitSelectorStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.UNIT_SELECTOR;
}

export function isUnitReferenceStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is UnitReferenceStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.UNIT_REFERENCE;
}

export function isMeasurementAmountStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is MeasurementAmountStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.MEASUREMENT_AMOUNT;
}

export function isContainersStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is ContainersStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.CONTAINERS;
}

export function isBoxStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is BoxStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.BOX;
}

export function isPlateStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is PlateStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.PLATE;
}

export function isCustomStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is CustomStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.CUSTOM;
}

export function isIngredientComponentLotStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): boolean {
  return (
    structuredTableColumn.columnGroup === StructuredTableColumnGroups.INGREDIENT_COMPONENT_LOT_STORAGE ||
    structuredTableColumn.columnType === TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT
  );
}

export function isIngredientNotesStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): boolean {
  return structuredTableColumn.columnType === TableColumnType.INGREDIENT_NOTES;
}

export function isProcedureConditionReplicatesStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is ProcedureConditionReplicatesStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.PROCEDURE_CONDITION_REPLICATES;
}

export function isProcessTaskStatusStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is ProcessTaskStatusStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.PROCESS_TASK_STATUS;
}

export function isProcessTaskStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is ProcessTaskStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.PROCESS_TASK;
}

export function isProcessOutputStructuredTableColumn(
  structuredTableColumn: StructuredTableColumn
): structuredTableColumn is ProcessOutputStructuredTableColumn {
  return structuredTableColumn.columnType === TableColumnType.PROCESS_OUTPUT;
}

export function isStorableStructuredTableColumn(structuredTableColumn: StructuredTableColumn): boolean {
  return (
    (isTagValueStructuredTableColumn(structuredTableColumn) &&
      structuredTableColumn.schemaFieldType === FieldType.STORABLE_LINK) ||
    isContainersStructuredTableColumn(structuredTableColumn) ||
    isBoxStructuredTableColumn(structuredTableColumn) ||
    isPlateStructuredTableColumn(structuredTableColumn) ||
    isParentStorableStructuredTableColumn(structuredTableColumn)
  );
}

/**
 * Return true for any column type that can be inserted into a finalized table
 *
 * Agent keep in sync: benchling/notebook/structured_tables/logic/column_logic.py:is_editable_insertable_column
 */
export function isInsertableStructuredTableColumn(structuredTableColumn: StructuredTableColumn): boolean {
  return (
    isTagValueStructuredTableColumn(structuredTableColumn) ||
    isIngredientComponentLotStructuredTableColumn(structuredTableColumn) ||
    isIngredientNotesStructuredTableColumn(structuredTableColumn) ||
    isCustomStructuredTableColumn(structuredTableColumn) ||
    isProcedureConditionReplicatesStructuredTableColumn(structuredTableColumn) ||
    (getColumnAdapter(structuredTableColumn.columnType)?.isUserConfigurable ?? false)
  );
}
